/**
 * File Validation Utilities for Batch Processing
 *
 * Validates uploaded files for batch processing
 */

/**
 * Allowed file types
 */
export const ALLOWED_FILE_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.csv',
  '.xls',
  '.xlsx'
];

/**
 * Maximum file size (100MB)
 */
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes

/**
 * Validate file type
 * @param {File} file - The file to validate
 * @returns {boolean} - Whether the file type is valid
 */
export const isValidFileType = (file) => {
  if (!file) return false;

  const fileType = file.type;
  const fileName = file.name.toLowerCase();

  // Check by MIME type
  if (ALLOWED_FILE_TYPES.includes(fileType)) {
    return true;
  }

  // Check by file extension
  return ALLOWED_FILE_TYPES.some(type =>
    type.startsWith('.') && fileName.endsWith(type)
  );
};

/**
 * Validate file size
 * @param {File} file - The file to validate
 * @returns {boolean} - Whether the file size is valid
 */
export const isValidFileSize = (file) => {
  if (!file) return false;
  return file.size <= MAX_FILE_SIZE;
};

/**
 * Get file extension
 * @param {File} file - The file
 * @returns {string} - File extension
 */
export const getFileExtension = (file) => {
  if (!file) return '';
  const fileName = file.name;
  const lastDot = fileName.lastIndexOf('.');
  return lastDot !== -1 ? fileName.substring(lastDot + 1).toLowerCase() : '';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Validate complete file
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateFile = (file) => {
  const errors = [];

  if (!file) {
    errors.push('Please select a file');
    return { isValid: false, errors };
  }

  if (!isValidFileType(file)) {
    errors.push('Invalid file type. Please upload a CSV or Excel file (.csv, .xls, .xlsx)');
  }

  if (!isValidFileSize(file)) {
    errors.push(`File size exceeds maximum limit of ${formatFileSize(MAX_FILE_SIZE)}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate batch upload request
 * @param {File} file - The file
 * @param {string} team - Selected team
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export const validateBatchUpload = (file, team) => {
  const errors = {};

  if (!file) {
    errors.file = 'File is required';
  } else {
    const fileValidation = validateFile(file);
    if (!fileValidation.isValid) {
      errors.file = fileValidation.errors.join(', ');
    }
  }

  if (!team || team.trim() === '') {
    errors.team = 'Team selection is required';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

